import { useEffect, useRef, useState } from 'react';
import type { ErpMatchApplyResult, ErpMatchProgress, ErpUndoResult, ErpUploadedRow } from '../../../api';
import { erpApi } from '../../../api';
import { useIdempotencyKey } from '../../../idempotency';
import { Callout, Table } from '../../primitives';
import { ErrorNotice } from '../../ui';
import { formatNumber } from '../labels';
import { countOf, defaultDelimiter, refusalText, saveBlob } from './matchingCopy';

const POLL_MS = 2500;

/** A row of the batch the person did not send, with why: the file's own row when there is one. */
export interface LeftOutRow {
  sku: string;
  articleCode: string;
  reason: string;
  uploaded?: ErpUploadedRow;
}

/**
 * What a batch did, from the store's own answers (CO-170). Applying only asks
 * the store, so the result starts as "waiting" and fills in as the store
 * confirms or refuses each product; nothing is counted as saved before it is.
 * The four numbers are the ones a person checks against their file: saved,
 * already matched, failed, and left out (rows that were invalid, not ticked,
 * or replacements that were not chosen). An undo asks the store to put back
 * what the batch replaced.
 */
export function BatchResult({
  connectionId,
  result,
  leftOut,
  rejectedDownload,
  alreadyMatched = 0,
  onChanged,
}: {
  connectionId: string;
  result: ErpMatchApplyResult;
  /** Rows that were not sent before applying: invalid, or not ticked. */
  leftOut: LeftOutRow[];
  /** Offered where the rows came from a file: a file with the original columns and why each was left out. */
  rejectedDownload?: boolean;
  /** Rows that were already matched before the batch was sent (an imported file's unchanged rows). */
  alreadyMatched?: number;
  /** The store was asked something: the list is read again. */
  onChanged: () => void;
}) {
  const [progress, setProgress] = useState<ErpMatchProgress | null>(null);
  const [undo, setUndo] = useState<ErpUndoResult | null>(null);
  const [undoProgress, setUndoProgress] = useState<ErpMatchProgress | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const idempotency = useIdempotencyKey();
  const changed = useRef(onChanged);
  changed.current = onChanged;

  // The store answers within a minute or so: read the requests until it has.
  const ids = result.pending_ids;
  const finished = progress?.done === true;
  useEffect(() => {
    if (ids.length === 0 || finished) return undefined;
    let cancelled = false;
    const read = async () => {
      try {
        const next = await erpApi.matchProgress(connectionId, ids);
        if (cancelled) return;
        setProgress(next);
        if (next.done) changed.current();
      } catch {
        // Tried again on the next tick.
      }
    };
    void read();
    const handle = window.setInterval(() => void read(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [connectionId, ids, finished]);

  const undoIds = undo?.pending_ids ?? [];
  const undoDone = undoProgress?.done === true;
  useEffect(() => {
    if (undoIds.length === 0 || undoDone) return undefined;
    let cancelled = false;
    const read = async () => {
      try {
        const next = await erpApi.matchProgress(connectionId, undoIds);
        if (cancelled) return;
        setUndoProgress(next);
        if (next.done) changed.current();
      } catch {
        // Tried again on the next tick.
      }
    };
    void read();
    const handle = window.setInterval(() => void read(), POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [connectionId, undoIds, undoDone]);

  async function undoAll() {
    setUndoing(true);
    setError(null);
    try {
      const done = await erpApi.undoMatches(connectionId, ids, idempotency.key());
      idempotency.settle();
      setUndo(done);
      changed.current();
    } catch (cause) {
      idempotency.settle(cause);
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setUndoing(false);
    }
  }

  const requested = result.requested;
  const confirmed = progress?.confirmed ?? 0;
  const waiting = requested === 0 ? 0 : (progress ? progress.waiting : requested);
  const refused = (progress?.rejected ?? 0) + (progress?.expired ?? 0);
  const failedBefore = result.rows.filter((row) => row.outcome === 'failed');
  const failed = refused + failedBefore.length;
  const excluded = leftOut.length + result.excluded;
  const storeFailures = progress?.failures ?? [];

  async function downloadRejected() {
    const rows: (ErpUploadedRow & { reason: string })[] = [];
    for (const row of leftOut) {
      if (row.uploaded) rows.push({ ...row.uploaded, reason: row.reason });
    }
    // What the store refused, and what failed when it was checked again, with the
    // file's own row where there is one.
    const byKey = new Map(leftOut.map((row) => [`${row.sku}\u0000${row.articleCode}`, row]));
    const seen = new Set(rows.map((row) => `${row.code_ecom}\u0000${row.code_erp}`));
    const add = (sku: string, article: string, reason: string) => {
      const original = byKey.get(`${sku}\u0000${article}`)?.uploaded;
      const entry = { code_ecom: original?.code_ecom ?? sku, name_ecom: original?.name_ecom ?? '', code_erp: original?.code_erp ?? article, name_erp: original?.name_erp ?? '', reason };
      const key = `${entry.code_ecom}\u0000${entry.code_erp}`;
      if (!seen.has(key)) {
        seen.add(key);
        rows.push(entry);
      }
    };
    for (const row of failedBefore) add(row.sku, row.article_code, row.message ?? refusalText(row.reason));
    for (const failure of storeFailures) add(failure.sku, failure.requested_code, `The store did not save it: ${refusalText(failure.reason)}`);
    try {
      const file = await erpApi.rejectedMatchesFile(connectionId, { format: 'xlsx', delimiter: defaultDelimiter(), rows });
      saveBlob(file.blob, file.filename);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    }
  }

  const headline = requested === 0
    ? 'Nothing needed saving'
    : !finished
      ? `Saving ${countOf(requested, 'match', 'matches')} in the store`
      : failed === 0
        ? `Saved ${countOf(confirmed, 'match', 'matches')}`
        : `Saved ${countOf(confirmed, 'match', 'matches')}, ${formatNumber(failed)} not saved`;
  const tone = requested === 0 ? 'info' : !finished ? 'info' : failed === 0 ? 'success' : 'warning';
  const canUndo = requested > 0 && undo === null && confirmed + waiting > 0;
  const notRejectedRows = failedBefore.length + storeFailures.length + leftOut.filter((row) => row.uploaded).length;

  return (
    <div className="match-result" data-testid="match-batch-result">
      <Callout tone={tone} title={headline} testId="match-batch-headline">
        {requested > 0 && !finished
          ? 'You can close this; the matches are still saved.'
          : requested === 0
            ? 'Every selected pair was already matched, or could not be sent.'
            : null}
      </Callout>
      <dl className="match-result__counts" aria-label="What the batch did" data-testid="match-batch-counts">
        <div>
          <dt>Saved in the store</dt>
          <dd className="tabular-nums" data-testid="match-count-saved">
            {formatNumber(confirmed)}
          </dd>
        </div>
        {!finished && requested > 0 ? (
          <div>
            <dt>Waiting for the store</dt>
            <dd className="tabular-nums" data-testid="match-count-waiting">
              {formatNumber(waiting)}
            </dd>
          </div>
        ) : null}
        <div>
          <dt>Already matched</dt>
          <dd className="tabular-nums" data-testid="match-count-unchanged">
            {formatNumber(result.unchanged + alreadyMatched)}
          </dd>
        </div>
        <div>
          <dt>Failed</dt>
          <dd className="tabular-nums" data-testid="match-count-failed">
            {formatNumber(failed)}
          </dd>
        </div>
        <div>
          <dt>Left out</dt>
          <dd className="tabular-nums" data-testid="match-count-excluded">
            {formatNumber(excluded)}
          </dd>
        </div>
      </dl>

      {failedBefore.length + storeFailures.length > 0 ? (
        <Table label="Matches that failed" compact className="match-plan" wrapperClassName="match-plan__scroll">
          <caption className="visually-hidden">Matches that failed, and why</caption>
          <thead>
            <tr>
              <th scope="col">Product SKU</th>
              <th scope="col">ERP article</th>
              <th scope="col">Why</th>
            </tr>
          </thead>
          <tbody>
            {failedBefore.map((row) => (
              <tr key={`f:${row.sku}:${row.article_code}`}>
                <td>
                  <code>{row.sku}</code>
                </td>
                <td>
                  <code>{row.article_code}</code>
                </td>
                <td>{row.message ?? refusalText(row.reason)}</td>
              </tr>
            ))}
            {storeFailures.map((failure) => (
              <tr key={`s:${failure.sku}:${failure.requested_code}`}>
                <td>
                  <code>{failure.sku}</code>
                </td>
                <td>
                  <code>{failure.requested_code}</code>
                </td>
                <td>The store did not save it: {refusalText(failure.reason)}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : null}

      {undo ? (
        <Callout tone={undo.requested === 0 ? 'warning' : undoDone ? (undoProgress && undoProgress.rejected + undoProgress.expired > 0 ? 'warning' : 'success') : 'info'} title="Undo" testId="match-undo-result">
          {undo.requested > 0
            ? `${undoDone ? 'The store put back' : 'The store was asked to put back'} ${countOf(undo.requested, 'product')}.`
            : 'Nothing was undone.'}
          {undo.skipped > 0 ? ` ${countOf(undo.skipped, 'product')} ${undo.skipped === 1 ? 'was' : 'were'} left as ${undo.skipped === 1 ? 'it is' : 'they are'}: ${undo.rows.find((row) => row.outcome === 'skipped')?.message ?? ''}` : ''}
        </Callout>
      ) : null}
      <ErrorNotice error={error} />

      <div className="match-result__actions">
        {rejectedDownload && notRejectedRows > 0 ? (
          <button type="button" className="button button--secondary" onClick={() => void downloadRejected()} data-testid="download-rejected">
            Download the {countOf(notRejectedRows, 'row')} not saved
          </button>
        ) : null}
        {canUndo ? (
          <button type="button" className="button button--secondary" disabled={undoing} onClick={() => void undoAll()} data-testid="match-undo-all">
            {undoing ? 'Asking the store' : `Undo ${countOf(requested, 'match', 'matches')}`}
          </button>
        ) : null}
      </div>
    </div>
  );
}
